import { logger } from "@/utils/logger";
/**
 * Phone Lead Validation Utility
 * 
 * Validates that a phone number detected in AI Contact data:
 * 1. Was actually provided by the USER (not fabricated by the AI)
 * 2. Is NOT one of the dealer's own phone numbers (sales, service, parts, etc.)
 */

/**
 * Normalize a phone string to just digits (removes country code prefix "1" if 11 digits)
 */
const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  // Remove leading "1" country code if 11 digits
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

/**
 * Check if a phone number appears in any user message in the conversation.
 * We normalize both the target phone and scan user messages for matching digit sequences.
 */
const phoneExistsInUserMessages = (
  phone: string,
  messages: Array<{ role: string; content: string }>
): boolean => {
  const normalizedTarget = normalizePhone(phone);
  if (normalizedTarget.length < 7) return false; // Too short to be a real phone

  const userMessages = messages.filter(m => m.role === 'user');

  for (const msg of userMessages) {
    // Extract all digit sequences from the user message
    const digitSequences = msg.content.replace(/[^\d]/g, ' ').split(/\s+/).filter(Boolean);
    
    // Also try the full digits of the message (user might type "5141234567" without spaces)
    const allDigits = msg.content.replace(/\D/g, '');
    
    // Check if the normalized target appears as a substring in the full digit string
    if (allDigits.includes(normalizedTarget)) {
      return true;
    }

    // Also check with country code prefix
    if (allDigits.includes('1' + normalizedTarget)) {
      return true;
    }

    // Check individual sequences that could be phone-length
    for (const seq of digitSequences) {
      const normalizedSeq = seq.length === 11 && seq.startsWith('1') ? seq.slice(1) : seq;
      if (normalizedSeq === normalizedTarget) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if a phone number matches any of the dealer's known phone numbers.
 * Reads from localStorage key "Dealer_Phones" which stores {sales, service, parts}.
 * Also checks any other phone-like fields that may exist.
 */
const isDealerPhone = (phone: string): boolean => {
  const normalizedTarget = normalizePhone(phone);
  if (normalizedTarget.length < 7) return false;

  try {
    const storedPhones = localStorage.getItem('Dealer_Phones');
    if (!storedPhones) return false;

    const dealerPhones = JSON.parse(storedPhones);
    
    // Check all values in the dealer phones object
    for (const value of Object.values(dealerPhones)) {
      if (typeof value === 'string' && value.trim()) {
        const normalizedDealer = normalizePhone(value);
        if (normalizedDealer.length >= 7 && normalizedDealer === normalizedTarget) {
          logger.log('🎯🎯🎯 [PHONE VALIDATION] ❌ Phone matches a dealer phone');
          return true;
        }
      }
    }
  } catch (error) {
    console.error('🎯🎯🎯 [PHONE VALIDATION] Error reading dealer phones:', error);
  }

  return false;
};

/**
 * Validates whether a phone number from an AI Contact object is eligible for lead generation.
 * 
 * Returns { valid: true } if phone is OK for lead, or { valid: false, reason: string } if not.
 */
export const validatePhoneForLead = (
  phone: string | undefined,
  messages: Array<{ role: string; content: string }>
): { valid: boolean; reason?: string } => {
  // No phone = no phone validation needed (lead may still be valid via email)
  if (!phone || !phone.trim()) {
    return { valid: true };
  }

  // Check 1: Phone must appear in a USER message (not AI-generated)
  if (!phoneExistsInUserMessages(phone, messages)) {
    logger.log('🎯🎯🎯 [PHONE VALIDATION] ❌ Phone not found in any user message - likely AI-generated');
    return { valid: false, reason: 'Phone number was not provided by the user' };
  }

  // Check 2: Phone must NOT be the dealer's own phone
  if (isDealerPhone(phone)) {
    logger.log('🎯🎯🎯 [PHONE VALIDATION] ❌ Phone matches a dealer phone number');
    return { valid: false, reason: 'Phone number matches dealer phone' };
  }

  logger.log('🎯🎯🎯 [PHONE VALIDATION] ✅ Phone passed all validations');
  return { valid: true };
};
